import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { transferAPI, finishedGoodAPI, warehouseAPI, rawMaterialAPI } from '../utils/api';
import { ArrowRight, Plus, X, MoveRight } from 'lucide-react';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore';
import { localName, fmt, apiError, today } from '../utils/helpers';
import SectionHero from '../components/common/SectionHero';

const emptyForm = () => ({
  transfer_date: today(),
  item_type: 'product',
  finished_good_id: '',
  raw_material_id: '',
  from_warehouse_id: '',
  to_warehouse_id: '',
  quantity_rolls: '',
  quantity_sqm: '',
  notes: '',
});

export default function Transfers() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { user } = useAuthStore();
  const canEdit = ['admin', 'manager', 'production_manager'].includes(user?.role);

  const [transfers, setTransfers] = useState([]);
  const [goods, setGoods] = useState([]);
  const [mats, setMats] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    rawMaterialAPI.list().then(r => setMats(r.data)).catch(() => {});
    Promise.all([transferAPI.list(), finishedGoodAPI.list(), warehouseAPI.list()])
      .then(([tr, g, w]) => { setTransfers(tr.data); setGoods(g.data); setWarehouses(w.data); })
      .catch(() => toast.error(t('error')))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleSave = async () => {
    const isRaw = form.item_type === 'raw';
    if (!(isRaw ? form.raw_material_id : form.finished_good_id) || !form.from_warehouse_id || !form.to_warehouse_id || !form.quantity_rolls)
      return toast.error(t('fill_required'));
    if (form.from_warehouse_id === form.to_warehouse_id)
      return toast.error(t('same_warehouse_error'));
    setSaving(true);
    try {
      await transferAPI.create({ ...form, finished_good_id: isRaw ? null : form.finished_good_id, raw_material_id: isRaw ? form.raw_material_id : null, quantity_rolls: parseFloat(form.quantity_rolls), quantity_sqm: isRaw ? 0 : parseFloat(form.quantity_sqm) || 0 });
      toast.success(t('saved'));
      setShowModal(false);
      setForm(emptyForm());
      load();
    } catch (e) {
      toast.error(apiError(e, t));
    } finally {
      setSaving(false);
    }
  };

  const warehouseName = id => warehouses.find(w => w.id === id)?.name || '—';
  const goodName = tr => localName(tr, lang) || tr.finished_good;
  const isRaw = form.item_type === 'raw';
  const stockIn = wid => isRaw ? mats.find(m => m.id === form.raw_material_id)?.stocks.find(s => s.warehouse_id === wid)?.quantity
    : goods.find(g => g.id === form.finished_good_id)?.stocks.find(s => s.warehouse_id === wid)?.quantity_rolls;

  return (
    <div className="space-y-5">
      <SectionHero title={t('transfers')} subtitle={<>{transfers.length} {t('transfers')}</>}>
        {canEdit && (
          <button className="btn btn-primary" onClick={() => { setForm(emptyForm()); setShowModal(true); }}>
            <Plus size={16} /> {t('new_transfer')}
          </button>
        )}
      </SectionHero>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : transfers.length === 0 ? (
          <div className="empty-state">
            <MoveRight size={32} style={{ color: 'var(--text-muted)', margin: '0 auto 8px' }} />
            <p>{t('no_data')}</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>{t('product')}</th>
                  <th>{t('from_warehouse')} → {t('to_warehouse')}</th>
                  <th>{t('rolls')}</th>
                  <th className="hide-mobile">{t('sqm')}</th>
                  <th className="hide-mobile">{t('notes')}</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map(tr => (
                  <tr key={tr.id}>
                    <td>{tr.transfer_date}</td>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{goodName(tr)}{tr.item_type === 'raw' && <span className="badge badge-green" style={{ marginInlineStart: 6 }}>{t('raw_material')}</span>}</td>
                    <td>
                      <span className="flex items-center gap-1 text-xs">
                        <span className="badge badge-orange">{warehouseName(tr.from_warehouse_id)}</span>
                        <ArrowRight size={12} style={{ color: 'var(--text-muted)' }} />
                        <span className="badge badge-blue">{warehouseName(tr.to_warehouse_id)}</span>
                      </span>
                    </td>
                    <td style={{ fontFamily: 'monospace' }}>{fmt(tr.quantity_rolls)}{tr.item_type === 'raw' && <span style={{ color: 'var(--text-muted)' }}> {tr.unit}</span>}</td>
                    <td className="hide-mobile" style={{ fontFamily: 'monospace' }}>{fmt(tr.quantity_sqm)}</td>
                    <td className="hide-mobile" style={{ color: 'var(--text-muted)' }}>{tr.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="card-title">{t('new_transfer')}</h2>
              <button className="btn btn-ghost btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('date')}</label>
                  <input className="input" type="date" value={form.transfer_date} onChange={e => setForm(f => ({ ...f, transfer_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('type')}</label>
                  <select className="select" value={form.item_type} onChange={e => setForm(f => ({ ...f, item_type: e.target.value }))}>
                    <option value="product">{t('finished_goods')}</option><option value="raw">{t('raw_materials')}</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{isRaw ? t('raw_material') : t('product')} *</label>
                  {isRaw ? (
                  <select className="select" value={form.raw_material_id} onChange={e => setForm(f => ({ ...f, raw_material_id: e.target.value }))}>
                    <option value="">{t('select_material')}</option>
                    {mats.map(m => <option key={m.id} value={m.id}>{localName(m, lang)} ({m.unit})</option>)}
                  </select>) : (
                  <select className="select" value={form.finished_good_id} onChange={e => setForm(f => ({ ...f, finished_good_id: e.target.value }))}>
                    <option value="">{t('select_product')}</option>
                    {goods.map(g => <option key={g.id} value={g.id}>{localName(g, lang)}</option>)}
                  </select>)}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('from_warehouse')} *</label>
                  <select className="select" value={form.from_warehouse_id} onChange={e => setForm(f => ({ ...f, from_warehouse_id: e.target.value }))}>
                    <option value="">{t('select_warehouse')}</option>
                    {warehouses.map(w => { const st = stockIn(w.id); return <option key={w.id} value={w.id}>{w.name}{st != null ? ` (${fmt(st)})` : ''}</option>; })}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('to_warehouse')} *</label>
                  <select className="select" value={form.to_warehouse_id} onChange={e => setForm(f => ({ ...f, to_warehouse_id: e.target.value }))}>
                    <option value="">{t('select_warehouse')}</option>
                    {warehouses.filter(w => w.id !== form.from_warehouse_id).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{isRaw ? t('quantity') : t('rolls')} *</label>
                  <input className="input" type="number" min="0" step="any" value={form.quantity_rolls} onChange={e => setForm(f => ({ ...f, quantity_rolls: e.target.value }))} />
                </div>
                {!isRaw && <div className="form-group">
                  <label className="form-label">{t('sqm')}</label>
                  <input className="input" type="number" min="0" step="any" value={form.quantity_sqm} onChange={e => setForm(f => ({ ...f, quantity_sqm: e.target.value }))} />
                </div>}
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">{t('notes')}</label>
                <input className="input" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}  />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>{t('cancel')}</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? t('loading') : t('transfer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

